import React, { useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';

import { Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import Skeleton from '@material-ui/lab/Skeleton';
import { useQueryClient } from '@tanstack/react-query';
import { ScrollContext } from 'App';
import { ADD_FILTER, REMOVE_FILTER } from 'actions/actionTypes';
// components
import { resetAllAction, setFiltersAction, setTeamAction } from 'actions/globalFilters';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import CustomTagChipList from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Details/CustomTagChipList';
import { showOKRDetails } from 'components/RabbitOKRSidePanel/okrPanelControlSlice';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import useOKRConfig from 'components/hooks/useOKRConfig';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { startOfDay } from 'date-fns';
import { capitalize, startCase } from 'lodash';
// project data and models
import TeamModel from 'models/team';
// selectors
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';
import { getDefaultLocalStorageStatsFilters, getLocalStorageFilters, setLocalStorageFilters } from 'screens/Team';
import { getStartTSEndTSFromDateFilters } from 'screens/Team/TeamPageFilters/helper';
import { ObjectiveKRCell as ObjectiveKRCellResponsive } from 'screens/home/summary/components/Cards/Goals/components/ObjectiveKRCell';
import { selectTeamPageGlobalFilters } from 'selectors/globalFilters';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { themeColors } from 'themes/clients/baseTheme';
import { checkModuleEnabled } from 'utils/helpers/moduleAccess';

import {
  INPROGRESS_STATUS_COLOR_MAP,
  OBJECTIVE_LEVEL_TAGS,
  TASK_TYPES_2,
  TEAM_DURATION_VALUES,
  TEAM_TASKS_VIEW_TYPES,
  TEAM_TYPE,
} from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import { convertDateToString4, notNullOrUndefined } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import { localStorageKeys } from 'constants/localStorageKeys';
import { sendAppliedFilterEvent, sendViewTypeChangedEvent } from 'constants/mixpanelHelper';
import { minifyNumber } from 'constants/numberMinifyHelper';

import { getStartEndTimeFromDurationKey } from 'services/helpers/task';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { filtersReducersWithClearAll, initialFiltersState, RESET_ALL_FILTERS } from '../../../TeamPageFilters';
import buildTeamPageFilters from '../../../TeamPageFilters/buildTeamPageFilters';
import { getSelectedFiltersCountFromMap } from '../../../TeamPageFilters/helper';
// helpers
import { getEnabledTaskTypeMenuItemsForDefaultOption } from '../../../helper';
import { ObjectiveKRCell } from '../AgGridTreeView/components/CommonTreeCell/ObjectiveKRCell';
import { StatusCell } from '../AgGridTreeView/components/CommonTreeCell/StatusCell';
import { CustomHeaderCell } from '../AgGridTreeView/components/CustomHeaderCell';
import { HeaderCellTooltip } from '../AgGridTreeView/components/HeaderCellTooltip';
import { KebabTableMenuPanelsRevamp } from '../AgGridTreeView/components/KebabTableMenu';
import { LoadingOverlayComponent } from '../AgGridTreeView/components/LoadingOverlayComponent';
import { NoRowsOverlayComponent } from '../AgGridTreeView/components/NoRowsOverlayComponent';
import {
  goalsInsightsGroupByGoalsDataQueryKey,
  useGoalsInsightsGroupByGoalsQuery,
} from '../AgGridTreeView/hooks/dataHooks';
import '../AgGridTreeView/styles/Menu.css';
import '../AgGridTreeView/styles/TreeView.css';
import { generateKebabMenuOptionsRevamp, postProcessPopup } from '../AgGridTreeView/utils';

export function getTeamCountHeading(teamCount, viewType = '') {
  if (viewType === TEAM_TASKS_VIEW_TYPES.goalTree) return 'Goals Tree';
  if (teamCount <= 0) return 'Team Members';
  if (teamCount === 1) return '1 Team Member';
  return `${teamCount} Team Members`;
}

const UPDATE_TASKS_DATA_CHANGE = 'UPDATE_TASKS_DATA_CHANGE';

function tasksDataChangeReducer(state, action) {
  switch (action.type) {
    case UPDATE_TASKS_DATA_CHANGE:
      return { ...state, [action.data.type]: action.data.value };
    // case UPDATE_TASKS_RELOAD:
    //   const newState = { ...state };
    //   newState.reload[action.data.type] = action.data.value;
    //   return newState;
    default:
      return state;
  }
}

function useScrollToTopState(pathname) {
  const { current: scrollParentRef } = useContext(ScrollContext);

  useEffect(() => {
    // when you come on team page, make sure that you scroll to the top
    if (scrollParentRef) {
      scrollParentRef.scrollTo(0, 0);
    }
  }, [pathname, scrollParentRef]);
}

export function useViewTypeState(selectedTaskType, sideNavSelectedCohort, isGoalOptionSelected) {
  const localStorageViewType = getLocalStorageViewType(selectedTaskType);
  let defaultInitialView = localStorageViewType;
  if (isGoalOptionSelected && sideNavSelectedCohort?.type === 'CUSTOM_TYPE') {
    defaultInitialView = TEAM_TASKS_VIEW_TYPES.list;
    setLocalStorageViewType(selectedTaskType, defaultInitialView);
  }
  const [teamTasksViewType, setTeamTasksViewType] = useState(
    defaultInitialView ?? getDefaultViewType(selectedTaskType)
  );

  function handleTeamViewTypeChange(viewType) {
    if (!viewType) return;
    sendViewTypeChangedEvent({
      eventProperties: {
        type: viewType,
        source: selectedTaskType,
      },
    });

    setTeamTasksViewType(viewType);
    setLocalStorageViewType(selectedTaskType, viewType);
  }

  useEffect(() => {
    if (sideNavSelectedCohort?.type === 'CUSTOM_TYPE') {
      setTeamTasksViewType(TEAM_TASKS_VIEW_TYPES.list);
    }
  }, [sideNavSelectedCohort?.type]);
  return { teamTasksViewType, setTeamTasksViewType, handleTeamViewTypeChange };
}

export const getLocalStorageViewType = (type) => {
  const key =
    type === TASK_TYPES_2.goal ? localStorageKeys.teamPageGoalsViewType : localStorageKeys.teamPageTasksViewType;
  const val = localStorage.getItem(key);
  return val || null;
};

export const setLocalStorageViewType = (type, viewType) => {
  const key =
    type === TASK_TYPES_2.goal ? localStorageKeys.teamPageGoalsViewType : localStorageKeys.teamPageTasksViewType;
  localStorage.setItem(key, viewType);
};

export const getLocalStorageStatsFilters = (type) => {
  const key =
    type === TASK_TYPES_2.goal
      ? localStorageKeys.teamPageGoalsStatsFilters
      : localStorageKeys.teamPageTasksStatsFilters;
  const val = localStorage.getItem(key);

  return (val && JSON.parse(val)) || null;
};

function useTeamPageState({ sideNavSelectedCohort }) {
  const location = useLocation();
  const [teamCount, setTeamCount] = useState(0);
  // const [performanceStats, setPerformanceStats] = useState({ reliabilityScore: 0, growth: 0 });
  const [team, setTeam] = useState(null);
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);
  const personalTeam = loggedInEmployee.teams.filter((team) => team.teamType === TEAM_TYPE.personal)[0];
  const durationMenuItems = TEAM_DURATION_VALUES;
  const [selectedDurationId, setSelectedDurationValue] = useState('LASTYEAR');
  let selectedTaskTypeFromLs = localStorage.getItem(localStorageKeys.selectedTaskType);
  if (selectedTaskTypeFromLs && !checkModuleEnabled(selectedTaskTypeFromLs)) {
    // module not enabled, so not valid anymore
    selectedTaskTypeFromLs = null;
    localStorage.removeItem(localStorageKeys.selectedTaskType);
  }
  const [selectedTaskType, setSelectedTaskType] = useState(
    selectedTaskTypeFromLs || getEnabledTaskTypeMenuItemsForDefaultOption()[0].value
  );
  const isGoalOptionSelected = selectedTaskType === TASK_TYPES_2.goal;
  const { teamTasksViewType, setTeamTasksViewType, handleTeamViewTypeChange } = useViewTypeState(
    selectedTaskType,
    sideNavSelectedCohort,
    isGoalOptionSelected
  );

  const dispatch = useDispatch();
  const [tasksLoading, updateTasksLoading] = useState(false);
  const activeFilters = useSelector(selectTeamPageGlobalFilters, shallowEqual).filters;
  // will be used in Stats when treeview is selected
  const activeFiltersWithoutObjectiveFilter = activeFilters.filter((item) => item.type !== 'TIMEBOUND_OBJECTIVE');

  // const [activeFilters, activeFiltersDispatch] = useReducer(filtersReducer, []);
  const [tasksDataChanged, tasksDataChangeDispatch] = useReducer(tasksDataChangeReducer, {
    [TEAM_TASKS_VIEW_TYPES.card]: false,
    [TEAM_TASKS_VIEW_TYPES.list]: false,
  });
  useScrollToTopState(location.pathname);
  const [taskTags, setTaskTags] = useState(null);
  const [goalTags, setGoalTags] = useState(null);
  const [refreshData, setRefreshData] = useState(false);
  const addStatsFilter = (type, value, displayValue, operator) => {
    dispatch({
      type: ADD_FILTER,
      data: {
        type,
        value,
        displayValue,
        operator: operator || 'IN',
      },
    });
    // save to local storage too
    /* disabling local storage persistence. It requires a bit more info to save. */
    /* const currentStatsFilters = getLocalStorageStatsFilters(selectedTaskType) || [];
    setLocalStorageStatsFilters(selectedTaskType, [
      ...currentStatsFilters,
      {
        type,
        value,
        displayValue,
        operator: operator || 'IN'
      }
    ]); */
  };

  const updateIsTasksDataChanged = useCallback((type, value) => {
    tasksDataChangeDispatch({ type: UPDATE_TASKS_DATA_CHANGE, data: { type, value } });
  }, []);
  const updateIsCardViewDataChanged = useCallback(
    (value) => {
      updateIsTasksDataChanged(TEAM_TASKS_VIEW_TYPES.card, value);
    },
    [updateIsTasksDataChanged]
  );

  const removeStatsFilter = (type, value, displayValue, operator) => {
    sendAppliedFilterEvent({
      eventProperties: {
        filter: displayValue,
        source: selectedTaskType,
      },
    });
    dispatch({
      type: REMOVE_FILTER,
      data: {
        type,
        value,
        operator: operator || 'IN',
      },
    });
    /* disabling local storage persistence. It requires a bit more info to save. */
    /* let currentStatsFilters = getLocalStorageStatsFilters(selectedTaskType) || [];
    currentStatsFilters = currentStatsFilters.filter(item => item.value !== value);
    setLocalStorageStatsFilters(selectedTaskType, [...currentStatsFilters]); */
  };
  const [newFiltersState, newFiltersDispatch] = useReducer(
    // logger(filtersReducersWithClearAll),
    filtersReducersWithClearAll,
    initialFiltersState
  );

  useEffect(() => {
    const localStorageFilters = getLocalStorageFilters(selectedTaskType);
    const filters = localStorageFilters || initialFiltersState;
    // removing initial state of type = "TASKS" when GOALS is selectedTaskType
    if (isGoalOptionSelected) {
      filters.taskTypeFilters = {};
    }

    newFiltersDispatch({ type: RESET_ALL_FILTERS, data: filters });
  }, [selectedTaskType]);

  return {
    teamCount,
    setTeamCount,
    // performanceStats,
    // setPerformanceStats,
    team,
    setTeam,
    personalTeam,
    selectedDurationId,
    setSelectedDurationValue,
    setSelectedTaskType,
    isGoalOptionSelected,
    teamTasksViewType,
    dispatch,
    tasksLoading,
    updateTasksLoading,
    activeFilters,
    activeFiltersWithoutObjectiveFilter,
    tasksDataChanged,
    taskTags,
    setTaskTags,
    goalTags,
    setGoalTags,
    refreshData,
    setRefreshData,
    setTeamTasksViewType,
    addStatsFilter,
    updateIsTasksDataChanged,
    updateIsCardViewDataChanged,
    removeStatsFilter,
    handleTeamViewTypeChange,
    selectedTaskType,
    loggedInEmployee,
    durationMenuItems,
    activeFiltersDispatch: dispatch,
    newFiltersState,
    newFiltersDispatch,
  };
}

export function getDefaultViewType(selectedTaskType) {
  return selectedTaskType === TASK_TYPES_2.goal ? TEAM_TASKS_VIEW_TYPES.list : TEAM_TASKS_VIEW_TYPES.card;
}

function getCompletedOnDurationFromFilters(newFiltersState) {
  const selectedCompletedFiltersCount = getSelectedFiltersCountFromMap(newFiltersState.completedOnFilters);
  return selectedCompletedFiltersCount > 0
    ? getStartTSEndTSFromDateFilters(newFiltersState.completedOnFilters)
    : getStartEndTimeFromDurationKey('LASTYEAR');
}

const defaultRowsPerPage = 10;

const defaultRowsPerPageForGoals = {
  [localStorageKeys.rabbitGoalsAuditTableRowsPerPage]: defaultRowsPerPage,
};

const generateAttributeFilter = (activeFilters = []) => {
  const data = activeFilters.map(({ value }) => value);
  return {
    type: 'STATUS',
    operatorType: 'IN',
    data,
  };
};

const GROUP_BY = {
  uuid: null,
  keyName: 'objective',
  displayName: 'Objective',
  enabled: true,
};

const SPLIT_BY = {
  uuid: null,
  keyName: 'none',
  displayName: 'None',
  enabled: true,
};

const CellContainer = styled(Flex)`
  height: 100%;
  align-items: center;
  justify-content: center;
  width: 100%;
  justify-content: ${(props) => props.justify};
`;

export const ChildrenObjectiveCountComponent = ({ data }) => {
  const { childObjectiveCount = 0 } = data ?? {};

  return (
    <CellContainer justify="flex-end">
      <TextBodyMedium>{childObjectiveCount || 0}</TextBodyMedium>
    </CellContainer>
  );
};

export const ChildKrComponent = ({ data }) => {
  const { childKrCount = 0 } = data ?? {};

  return (
    <CellContainer justify="flex-end">
      <TextBodyMedium>{childKrCount || 0}</TextBodyMedium>
    </CellContainer>
  );
};

export const CheckinCountComponent = ({ data }) => {
  const { selfCheckInCount = 0 } = data ?? {};

  return (
    <CellContainer justify="flex-end">
      <TextBodyMedium>{selfCheckInCount || 0}</TextBodyMedium>
    </CellContainer>
  );
};

export const CheckinChildrenCountComponent = ({ data }) => {
  const { checkInCount = 0 } = data ?? {};

  return (
    <CellContainer justify="flex-end">
      <TextBodyMedium>{checkInCount || 0}</TextBodyMedium>
    </CellContainer>
  );
};

export const LastCheckInComponent = ({ data }) => {
  const { lastCheckIn } = data ?? {};

  return (
    <CellContainer justify="flex-start">
      {notNullOrUndefined(lastCheckIn) ? (
        <TextBodyMedium>{convertDateToString4(lastCheckIn)}</TextBodyMedium>
      ) : (
        <TextBodyMedium>Never</TextBodyMedium>
      )}
    </CellContainer>
  );
};

export const LastCheckInChildrenComponent = ({ data }) => {
  const { latestChildCheckInTS, childKrCount = 0, childObjectiveCount = 0 } = data ?? {};
  const hasNoChild = childKrCount + childObjectiveCount === 0;

  return (
    <CellContainer justify="flex-start">
      {notNullOrUndefined(latestChildCheckInTS) ? (
        <TextBodyMedium>{convertDateToString4(latestChildCheckInTS)}</TextBodyMedium>
      ) : hasNoChild ? (
        <TextBodyMedium>NA</TextBodyMedium>
      ) : (
        <TextBodyMedium>Never</TextBodyMedium>
      )}
    </CellContainer>
  );
};

function GroupByObjectiveInsightsTable({
  gridRef,
  attributeFilter: defaultAttributeFilter,
  pageType = 'TEAM_HUB',
  selectedTimelines,
  groupBy = GROUP_BY,
  setChartData = () => {},
  gridStatePersistenceKey = null,
  onModalUpdated = () => {},
}) {
  const location = useLocation();
  const history = useHistory();
  const page = useViewPageName();

  const gridApi = gridRef?.current?.api;
  const dispatch = useDispatch();

  const sideNavSelectedCohort = useSelectedView();
  const { id: paramsTeamId, name: teamName } = sideNavSelectedCohort ?? {};
  // timebound details of newly created objective
  const attributeFilter = defaultAttributeFilter || generateAttributeFilter(activeFilters);
  const { team, setTeam, refreshData, setRefreshData, activeFiltersDispatch, newFiltersState } = useTeamPageState({
    sideNavSelectedCohort,
  });

  const activeFilters = [];

  const [duration, setDuration] = useState(getCompletedOnDurationFromFilters(newFiltersState));
  const timeBoundPeriod = selectedTimelines?.[0] ?? null;

  const { data, isLoading, isError } = useGoalsInsightsGroupByGoalsQuery({
    attributeFilter,
    cohort: sideNavSelectedCohort,
    groupByKeys: groupBy,
    splitByKeys: SPLIT_BY,
    orderBy: null,
    timeBounds: [timeBoundPeriod],
    isHierarchyView: true,
    pageType,
  });
  const queryClient = useQueryClient();

  const refetchInsightMetricsGroupByGoals = () => {
    queryClient.invalidateQueries(
      goalsInsightsGroupByGoalsDataQueryKey({
        cohort: sideNavSelectedCohort,
        groupByKeys: groupBy,
        splitByKeys: SPLIT_BY,
        orderBy: null,
        timeBounds: [timeBoundPeriod],
        attributeFilter,
        isHierarchyView: true,
        pageType,
      })
    );
  };

  const [rowData, setRowData] = useState(data);

  // using useState to save grid row data because without useState loading overlay of ag grid doesn't work. Ag grid is not able to correctly detect when we are loading data and when is our data empty
  useEffect(() => {
    setRowData(data);
  }, [data]);

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: true,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: '',
    }),
    []
  );

  const getProgressTooltip = () =>
    `${objectivesName} progress includes ${modifyString(
      moduleName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} that may not be visible in the filtered list. This is to ensure everyone has right view on the ${modifyString(
      objectivesName,
      StringModifier.SENTENCE_CASE
    )}'s current progress.`;

  const getStatusTooltip = () =>
    `Hover on the chart to visualise the status distribution of all the child ${modifyString(
      keyResultsName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} for the ${modifyString(moduleName, StringModifier.SENTENCE_CASE)}`;

  const getChildObjectiveTooltip = () =>
    `Shows the number of child ${modifyString(
      objectivesName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} aligned to the ${modifyString(moduleName, StringModifier.SENTENCE_CASE)}`;

  const getChildKRTooltip = () =>
    `Shows the number of child ${modifyString(
      keyResultsName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} aligned to the ${modifyString(moduleName, StringModifier.SENTENCE_CASE)}`;

  const getSelfCheckInCountTooltip = () =>
    `Shows the number of direct check-ins made on the selected ${modifyString(
      moduleName,
      StringModifier.SENTENCE_CASE
    )}. Only direct changes made to the status or progress of the ${modifyString(
      moduleName,
      StringModifier.SENTENCE_CASE
    )} would be regarded as check-ins. We would not be accounting for any changes made to child ${modifyString(
      moduleName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} in this column.`;

  const getCheckInCountTooltip = () =>
    `Shows the number of check-ins made on the entire ${modifyString(
      moduleName,
      StringModifier.SENTENCE_CASE
    )} tree with the selected ${modifyString(
      moduleName,
      StringModifier.SENTENCE_CASE
    )} at the parent level. Any change in the status or progress of child ${modifyString(
      moduleName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} would also be recorded as a check-in and the value would be reflected in this column.`;

  const getLastCheckInTooltip = () =>
    `Shows the date of the last check-in made on the selected ${modifyString(
      moduleName,
      StringModifier.SENTENCE_CASE
    )}.Only direct changes made to the status or progress of the ${modifyString(
      moduleName,
      StringModifier.SENTENCE_CASE
    )} would be regarded as check-ins. We would not be accounting for any changes made to child ${modifyString(
      moduleName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} in this column.`;

  const getLastChildCheckInTooltip = () =>
    `Shows the date of the last check-in made on the entire ${modifyString(
      moduleName,
      StringModifier.SENTENCE_CASE
    )} tree with the selected ${modifyString(
      moduleName,
      StringModifier.SENTENCE_CASE
    )} at the parent level. Any change in the status or progress of child ${modifyString(
      moduleName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} would also be recorded as a check-in and the same would be reflected in this column.`;

  const ProgressComponent = ({ data }) => {
    const { avgProgressPercentage = 0, progressLevel = 'ON_TRACK' } = data ?? {};
    let textColor = INPROGRESS_STATUS_COLOR_MAP[progressLevel]?.textColor;
    textColor = textColor === themeColors.common.white ? themeColors.text.g1 : textColor;
    return (
      <CellContainer justify="flex-end">
        <TextBodyMedium color={textColor} padding="2px 8px 2px 8px" borderRadius="4px">
          {minifyNumber(avgProgressPercentage)}%
        </TextBodyMedium>
      </CellContainer>
    );
  };

  const columnDefs = useMemo(
    () => [
      // we're using the auto group column by default!
      {
        field: 'type',
        headerName: 'Type',
        hide: true,
        valueGetter: (data) => data.data?.type,
        suppressColumnsToolPanel: true,
        colId: 'table-type',
      },
      {
        field: 'toIds',
        headerName: 'Owner',
        hide: true,
        suppressColumnsToolPanel: true,
        colId: 'table-owner',
        valueGetter: (data) =>
          data.data?.toIds?.map(({ ownerType, displayName }) => ownerType === 'OWNER' && displayName),
      },
      {
        field: 'toIds',
        headerName: 'Collaborators',
        hide: true,
        suppressColumnsToolPanel: true,
        colId: 'table-collaborators',
        valueGetter: (data) =>
          data.data?.toIds?.map(({ displayName, ownerType }) => ownerType === 'COLLABORATOR' && displayName),
      },

      {
        field: 'childObjectiveCount',
        headerName: `No. of Child ${modifyString(objectivesName, StringModifier.PLURAL_TITLE_CASE)}`,
        headerTooltip: getChildObjectiveTooltip(),
        tooltipComponent: HeaderCellTooltip,
        tooltipComponentParams: { showColumnName: true },
        width: 120,
        hide: true,
        filter: 'agNumberColumnFilter',
        type: 'numericColumn',
        filterValueGetter: (params) => params?.data?.childObjectiveCount ?? 0,
        valueGetter: (params) => params.data?.childObjectiveCount ?? 0,
        cellRenderer: ChildrenObjectiveCountComponent,
        colId: 'table-child-objective-count',
      },
      {
        field: 'childKrCount',
        filter: 'agNumberColumnFilter',
        headerName: `No. of child ${modifyString(keyResultsName, StringModifier.PLURAL_TITLE_CASE)}`,
        headerTooltip: getChildKRTooltip(),
        tooltipComponent: HeaderCellTooltip,
        tooltipComponentParams: { showColumnName: true },
        width: 120,
        type: 'numericColumn',
        filterValueGetter: (params) => params?.data?.childKrCount ?? 0,
        valueGetter: (params) => params.data?.childKrCount ?? 0,
        cellRenderer: ChildKrComponent,
        colId: 'table-child-kr-count',
      },
      {
        field: 'checkinCount',
        headerName: `No. of Check-ins on the ${moduleName}`,
        headerTooltip: getSelfCheckInCountTooltip(),
        tooltipComponent: HeaderCellTooltip,
        tooltipComponentParams: { showColumnName: true },
        width: 120,
        filter: 'agNumberColumnFilter',
        type: 'numericColumn',
        filterValueGetter: (params) => params?.data?.selfCheckInCount ?? 0,
        valueGetter: (params) => params?.data?.selfCheckInCount ?? 0,
        cellRenderer: CheckinCountComponent,
        colId: 'table-checkin-count',
      },
      {
        field: 'checkinCount',
        headerName: `No. of Check-ins on the ${moduleName} Tree`,
        headerTooltip: getCheckInCountTooltip(),
        tooltipComponent: HeaderCellTooltip,
        hide: true,
        tooltipComponentParams: { showColumnName: true },
        width: 120,
        filter: 'agNumberColumnFilter',
        type: 'numericColumn',
        filterValueGetter: (params) => params?.data?.checkInCount ?? 0,
        valueGetter: (params) => params?.data?.checkInCount ?? 0,
        cellRenderer: CheckinChildrenCountComponent,
        colId: 'table-checkin-children-count',
      },
      {
        field: 'lastCheckin',
        headerName: 'Last Check-In',
        headerTooltip: getLastCheckInTooltip(),
        tooltipComponent: HeaderCellTooltip,
        tooltipComponentParams: { showColumnName: true },
        width: 140,
        filter: 'agDateColumnFilter',
        filterValueGetter: (params) =>
          params?.data?.lastCheckIn ? startOfDay(new Date(params?.data?.lastCheckIn)) : null,

        valueGetter: (params) => (params?.data?.lastCheckIn ? convertDateToString4(params?.data?.lastCheckIn) : ''),
        cellRenderer: LastCheckInComponent,
        colId: 'table-last-checkin',
      },
      {
        field: 'lastCheckin',
        headerName: `Last Check-in on the ${moduleName} Tree`,
        headerTooltip: getLastChildCheckInTooltip(),
        tooltipComponent: HeaderCellTooltip,
        tooltipComponentParams: { showColumnName: true },
        hide: true,
        width: 140,
        filter: 'agDateColumnFilter',
        filterValueGetter: (params) =>
          params?.data?.latestChildCheckInTS ? startOfDay(new Date(params?.data?.latestChildCheckInTS)) : null,
        valueGetter: (params) =>
          params?.data?.latestChildCheckInTS ? convertDateToString4(params?.data?.latestChildCheckInTS) : '',
        cellRenderer: LastCheckInChildrenComponent,
        colId: 'table-last-checkin-children',
      },
      {
        // Make sure the field key or if valueGetter exists, they're being updated on data update else cell won't re-render
        // if you've other data in cell renderer
        field: 'startDate',
        headerName: 'Start Date',
        hide: true,
        minWidth: 130,
        maxWidth: 130,
        sortable: true,
        filter: 'agDateColumnFilter',
        filterValueGetter: (params) => (params?.data?.startDate ? startOfDay(new Date(params?.data?.startDate)) : null),
        valueGetter: (params) => (params?.data?.startDate ? convertDateToString4(params?.data?.startDate) : ''),
        cellRenderer: ({ data }) => {
          const { startDate } = data ?? {};

          return (
            <Flex alignItems="center" justifyContent="flex-start" h="100%" w="100%">
              {notNullOrUndefined(startDate) ? (
                <TextBodyMedium>{convertDateToString4(startDate)}</TextBodyMedium>
              ) : (
                <TextBodyMedium>Never</TextBodyMedium>
              )}
            </Flex>
          );
        },
        colId: 'table-start-date',
      },
      {
        // Make sure the field key or if valueGetter exists, they're being updated on data update else cell won't re-render
        // if you've other data in cell renderer
        field: 'dueDate',
        headerName: 'Due Date',
        hide: true,
        minWidth: 130,
        maxWidth: 130,
        sortable: true,
        filter: 'agDateColumnFilter',
        filterValueGetter: (params) => (params?.data?.dueDate ? startOfDay(new Date(params?.data?.dueDate)) : null),
        valueGetter: (params) => (params?.data?.dueDate ? convertDateToString4(params?.data?.dueDate) : ''),
        cellRenderer: ({ data }) => {
          const { dueDate } = data ?? {};

          return (
            <Flex alignItems="center" justifyContent="flex-start" h="100%" w="100%">
              {notNullOrUndefined(dueDate) ? (
                <TextBodyMedium>{convertDateToString4(dueDate)}</TextBodyMedium>
              ) : (
                <TextBodyMedium>Never</TextBodyMedium>
              )}
            </Flex>
          );
        },
        colId: 'table-due-date',
      },
      {
        // Make sure the field key or if valueGetter exists, they're being updated on data update else cell won't re-render
        // if you've other data in cell renderer
        field: 'progress',
        headerName: 'Progress',
        headerTooltip: getProgressTooltip(),
        tooltipComponent: HeaderCellTooltip,
        tooltipComponentParams: { showColumnName: true },
        width: 120,
        filter: 'agNumberColumnFilter',
        colId: 'table-progress',
        sortable: false,
        filterParams: {
          applyMiniFilterWhileTyping: true,
          getValue: (params) => Number(params?.data?.avgProgressPercentage?.toFixed(2) || 0),
          debounceMs: 200,
          filterOptions: ['equals', 'inRange'],
          suppressAndOrCondition: true,
        },
        valueGetter: (params) => params?.data?.progressLevel + params?.data?.avgProgressPercentage,
        cellRenderer: ProgressComponent,
      },
      {
        field: 'progress',
        headerName: `Child ${keyResultsName} Status`,
        headerTooltip: getStatusTooltip(),
        tooltipComponent: HeaderCellTooltip,
        tooltipComponentParams: { showColumnName: true },
        width: 120,
        filter: 'agMultiColumnFilter',
        colId: 'table-status',
        sortable: false,
        headerClass: 'truncate-header-ag-grid',
        filterParams: {
          filters: [
            {
              filter: 'agNumberColumnFilter',
              display: 'accordion',
              title: 'On-Track count',
              filterParams: {
                applyMiniFilterWhileTyping: true,
                debounceMs: 200,
                suppressAndOrCondition: true,
                defaultOption: 'equals',
                defaultJoinOperator: 'AND',
                suppressMiniFilter: false,
                suppressRemoveEntries: false,
                suppressSelectAll: false,
                getValue: (data) => data?.data?.progress?.progressCount?.ON_TRACK?.count ?? 0,
              },
            },
            {
              filter: 'agNumberColumnFilter',
              display: 'accordion',
              title: 'Off-Track count',
              filterParams: {
                applyMiniFilterWhileTyping: true,
                debounceMs: 200,
                suppressAndOrCondition: true,
                defaultOption: 'equals',
                defaultJoinOperator: 'AND',
                suppressMiniFilter: false,
                suppressRemoveEntries: false,
                suppressSelectAll: false,
                getValue: (data) => data?.data?.progress?.progressCount?.OFF_TRACK?.count ?? 0,
              },
            },
            {
              filter: 'agNumberColumnFilter',
              display: 'accordion',
              title: 'At-Risk count',
              filterParams: {
                applyMiniFilterWhileTyping: true,
                debounceMs: 200,
                suppressAndOrCondition: true,
                defaultOption: 'equals',
                defaultJoinOperator: 'AND',
                suppressMiniFilter: false,
                suppressRemoveEntries: false,
                suppressSelectAll: false,
                getValue: (data) => data?.data?.progress?.progressCount?.AT_RISK?.count ?? 0,
              },
            },
            {
              filter: 'agNumberColumnFilter',
              display: 'accordion',
              title: 'Closed count',
              filterParams: {
                applyMiniFilterWhileTyping: true,
                debounceMs: 200,
                suppressAndOrCondition: true,
                defaultOption: 'equals',
                defaultJoinOperator: 'AND',
                suppressMiniFilter: false,
                suppressRemoveEntries: false,
                suppressSelectAll: false,
                getValue: (data) => data?.data?.progress?.progressCount?.CLOSED?.count ?? 0,
              },
            },
          ],
        },
        valueGetter: (params) => params?.progress?.progressCount ?? {},
        cellRenderer: ({ data }) => {
          const { ifGoalChildExists, progress } = data ?? {};

          const { overallCount } = progress ?? {};

          if (!ifGoalChildExists || !overallCount) {
            const tooltipText = !overallCount
              ? `${moduleName}(s) marked as dependent in the child ${modifyString(
                  moduleName,
                  StringModifier.SENTENCE_CASE
                )} tree has no child ${modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)}`
              : `The ${modifyString(moduleName, StringModifier.SENTENCE_CASE)} has no child ${modifyString(
                  keyResultsName,
                  StringModifier.PLURAL_SENTENCE_CASE
                )}`;
            return (
              <Tooltip label={tooltipText}>
                <Flex alignItems="center" justifyContent="center" h="100%" w="100%">
                  <TextBodyMedium>NA</TextBodyMedium>
                </Flex>
              </Tooltip>
            );
          }
          return <StatusCell progress={progress} />;
        },
      },
      ...[
        {
          colId: 'customTags',
          field: 'customTags',
          headerName: 'Tags',
          hide: true,
          minWidth: 300,
          filter: 'agSetColumnFilter',
          sortable: false,
          filterParams: {
            applyMiniFilterWhileTyping: true,
            getValue: (params) => params?.data?.tagKeyList?.map((tagObj) => tagObj.tagName) || [],
          },
          valueGetter: (params) => params?.data?.tagKeyList?.map((tagObj) => tagObj.tagName) || [],
          cellRenderer: (params) =>
            params?.data?.tagKeyList?.length > 0 ? (
              <Flex alignItems="center" justifyContent="flex-start" h="100%" w="100%">
                <CustomTagChipList tags={params?.data?.tagKeyList || []} tagsLimit={3} />
              </Flex>
            ) : (
              <Flex alignItems="center" justifyContent="flex-start" h="100%" w="100%">
                <TextBodyMedium>None</TextBodyMedium>{' '}
              </Flex>
            ),
        },
      ],
    ],
    [keyResultsName, objectivesName]
  );
  const columnDefsResponsive = [
    {
      // Make sure the field key or if valueGetter exists, they're being updated on data update else cell won't re-render
      // if you've other data in cell renderer
      field: '',
      maxWidth: 25,
      headerName: '',
      pinned: 'right',
      lockPosition: 'right',
      cellClass: 'locked-col',
      suppressHeaderMenuButton: true,
      cellRenderer: KebabTableMenuPanelsRevamp,
      suppressColumnsToolPanel: true,
    },
  ];

  const defaultColDef = useMemo(
    () => ({
      menuTabs: ['filterMenuTab'],
      headerComponent: CustomHeaderCell,
    }),
    []
  );

  const autoGroupColumnDef = useMemo(
    () => ({
      headerName: moduleName,
      minWidth: 350,
      width: 350,
      flex: 1,
      filter: 'agMultiColumnFilter',
      colId: 'table-goal-name',
      filterParams: {
        filters: [
          {
            filter: 'agSetColumnFilter',
            display: 'accordion',
            title: 'Filter by Level',
            filterParams: {
              values: OBJECTIVE_LEVEL_TAGS.map(({ value }) => value),
              // this is used to display the value in the filter menu
              valueFormatter: ({ value }) => capitalize(startCase(value)),
              // this is used to get the key which is used to filter the data
              getValue: (data) => data.data.objectiveType,
            },
          },
          {
            display: 'accordion',
            title: `Filter by ${moduleName} Title`,
            getValue: (data) => data.data.title,
            filterParams: {
              // copy pasta from ag grid.
              // not touching it for now
              textFormatter: (r) => {
                if (r === null) return null;
                return r
                  .toLowerCase()
                  .replace(/[àáâãäå]/g, 'a')
                  .replace(/æ/g, 'ae')
                  .replace(/ç/g, 'c')
                  .replace(/[èéêë]/g, 'e')
                  .replace(/[ìíîï]/g, 'i')
                  .replace(/ñ/g, 'n')
                  .replace(/[òóôõö]/g, 'o')
                  .replace(/œ/g, 'oe')
                  .replace(/[ùúûü]/g, 'u')
                  .replace(/[ýÿ]/g, 'y');
              },
              filterOptions: ['contains', 'notContains'],
              debounceMs: 200,
              suppressAndOrCondition: true,
            },
          },
        ],
      },
      valueGetter: (data) => data.data ?? {},
      filterValueGetter: (data) => data.data.title ?? '',
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: ObjectiveKRCell,
      },
      cellStyle: (params) => ({ overflow: 'visible' }),
      sortable: false,
      comparator: (value1, value2) =>
        String(value1).toLocaleLowerCase().localeCompare(String(value2).toLocaleLowerCase()),
      equals: (okr1, okr2) => {
        const isTitleUnchanged = okr1.title === okr2.title;
        const isVisibilityUnchanged = okr1.visibilityType === okr2.visibilityType;
        const ifGoalChildExistsUnchanged = okr1.ifGoalChildExists === okr2.ifGoalChildExists;
        const isProgressTypeUnchanged = okr1.progressType === okr2.progressType;

        return isTitleUnchanged && isVisibilityUnchanged && ifGoalChildExistsUnchanged && isProgressTypeUnchanged;
      },
      cellClass: 'objective-kr-cell-ag-grid-wrap',
      autoHeight: true,
      wrapText: true,
      chartDataType: 'excluded',
    }),
    []
  );

  const autoGroupColumnDefResponsive = {
    headerName: moduleName,
    minWidth: 250,
    // flex: 1,
    filter: true,
    cellStyle: { height: 'auto' },
    cellRendererParams: {
      suppressCount: true,
      innerRenderer: ObjectiveKRCellResponsive,
    },

    //  hacky way to make sure that the auto group column is updated on the basis of the valueGetter
    valueGetter: (data) =>
      data?.data?.title + data?.data?.progressLevel + data?.data?.progress + data?.data?.avgProgressPercentage,
  };

  const getDataPath = useMemo(() => (data) => data.hierarchy, []);

  const getRowId = useCallback(({ data }) => data?.uuid, []);

  const rowClickHandler = ({ data, column, event }) => {
    const { type, uuid, unMasked } = data ?? {};
    if (!unMasked) {
      return;
    }
    dispatch(
      showOKRDetails({
        id: uuid,
        type,
        onDelete: refetchInsightMetricsGroupByGoals,
        onUpdate: refetchInsightMetricsGroupByGoals,
        history,
      })
    );
  };

  const getRowStyle = (params) => {
    if (params?.data?.unMasked) {
      return { cursor: 'pointer' };
    }
  };

  // had to keep new filter logic separate from the old, so that it works on mobile as well
  // TODO: remove old filter logic and only use this new one
  useEffect(() => {
    const newActiveFilters = buildTeamPageFilters(newFiltersState, true);
    setDuration((duration) => {
      const newDuration = getCompletedOnDurationFromFilters(newFiltersState);
      return JSON.stringify(duration) !== JSON.stringify(newDuration) ? newDuration : duration;
    });
    const localStorageStatsFilters = getDefaultLocalStorageStatsFilters(TASK_TYPES_2.goal) || [];
    activeFiltersDispatch(setFiltersAction([...newActiveFilters, ...localStorageStatsFilters]));
  }, [JSON.stringify(newFiltersState)]);

  useEffect(() => {
    const teamId = paramsTeamId;
    if (!teamId) return;

    dispatch(setTeamAction(teamId)).then(() => {
      setTeam(new TeamModel({ id: teamId, name: teamName }));
    });
  }, [dispatch, setTeam, paramsTeamId, teamName]);

  useEffect(
    () => () => {
      dispatch(resetAllAction());
    },
    [dispatch]
  );

  useEffect(() => {
    const { employeeFilters, ...otherTaskFilters } = newFiltersState;
    const { primaryPodFilters, objectiveFilters, ...otherGoalFilters } = otherTaskFilters;

    setLocalStorageFilters(TASK_TYPES_2.goal, otherGoalFilters);
  }, [newFiltersState]);

  useEffect(() => {
    if (sideNavSelectedCohort) {
      gridRef?.current?.api?.setFilterModel({});
    }
  }, [sideNavSelectedCohort]);

  useEffect(() => {
    setChartData((prev) => ({ ...prev, isLoading, rowData: data }));
    if (gridApi) {
      if (isLoading) {
        gridApi.setGridOption('loading', true);
      } else if (data && data.length === 0) {
        gridApi.showNoRowsOverlay();
      } else {
        gridApi.setGridOption('loading', false);
      }
    }
  }, [isLoading, data, gridApi]);

  const handleDataModelUpdated = useCallback(() => {
    const newRowData = [];
    gridRef?.current?.api?.forEachNodeAfterFilterAndSort((row) => {
      newRowData.push(row?.data);
    });

    setChartData((prev) => ({ ...prev, rowData: newRowData }));
  }, [gridRef, setChartData]);

  const refresh = () => {
    setRefreshData(true);
    setTimeout(() => {
      setRefreshData(false);
    }, 100);
  };

  useEffect(() => {
    refresh();
  }, [selectedTimelines]);

  const { OBJECTIVE_TYPE, IS_KR_CASCADING_ENABLED } = useOKRConfig();

  if (!team || !team?.id) {
    return <Skeleton height="30vh" />;
  }

  if (!sideNavSelectedCohort) {
    return <Skeleton height="30vh" />;
  }

  const HEADER_HEIGHT = '120';
  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      <AgGridTable
        key={gridStatePersistenceKey} // we want to re-initialize on change of key
        gridStatePersistenceKey={gridStatePersistenceKey}
        ref={gridRef}
        rowData={rowData}
        sideBar={isMobileView() ? false : sideBar}
        columnDefs={isMobileView() ? columnDefsResponsive : columnDefs}
        defaultColDef={defaultColDef}
        autoGroupColumnDef={isMobileView() ? autoGroupColumnDefResponsive : autoGroupColumnDef}
        treeData
        animateRows
        groupDefaultExpanded={-1}
        getDataPath={getDataPath}
        rowHeight={84}
        getRowStyle={getRowStyle}
        onCellClicked={rowClickHandler}
        getRowId={getRowId}
        suppressRowTransform
        loadingOverlayComponent={LoadingOverlayComponent}
        noRowsOverlayComponent={NoRowsOverlayComponent}
        noRowsOverlayComponentParams={{
          moduleName,
        }}
        tooltipShowDelay={500}
        postProcessPopup={postProcessPopup}
        suppressMenuHide // <- this is to force the menu icon to show
        pagination
        paginationAutoPageSize
        onModelUpdated={() => {
          handleDataModelUpdated();
          onModalUpdated();
        }}
        showSidebarToggle
        kebabMenuIsRowSpecific
        kebabMenuOptionsGenerator={(data) =>
          generateKebabMenuOptionsRevamp({
            page,
            data,
            dispatch,
            keyResultsName,
            objectivesName,
            queryClient,
            IS_KR_CASCADING_ENABLED,
            OBJECTIVE_TYPE,
            history,
            location,
            // selectedTimelines
          })
        }
      />
    </>
  );
}

export default GroupByObjectiveInsightsTable;
